// The line icons a skill can wear (the relay's SKILL_ICONS, same order) and Discover's
// categories (SkillsMP's slugs). The first icon is every new skill's default.
import type { Component } from "svelte";
import NotepadTextIcon from "@lucide/svelte/icons/notepad-text";
import CodeIcon from "@lucide/svelte/icons/code";
import ChartColumnIcon from "@lucide/svelte/icons/chart-column";
import FileTextIcon from "@lucide/svelte/icons/file-text";
import ListChecksIcon from "@lucide/svelte/icons/list-checks";
import MegaphoneIcon from "@lucide/svelte/icons/megaphone";
import MailIcon from "@lucide/svelte/icons/mail";
import CalendarIcon from "@lucide/svelte/icons/calendar";
import ImageIcon from "@lucide/svelte/icons/image";
import LandmarkIcon from "@lucide/svelte/icons/landmark";
import ScaleIcon from "@lucide/svelte/icons/scale";
import SquareTerminalIcon from "@lucide/svelte/icons/square-terminal";
import type { SkillIcon } from "$lib/plugins/agents/types.js";

export interface SkillIconOption {
  id: SkillIcon;
  label: string;
  icon: Component<{ class?: string; strokeWidth?: number; "aria-hidden"?: boolean | "true" }>;
}

export const SKILL_ICON_OPTIONS: readonly SkillIconOption[] = [
  { id: "default", label: "Default", icon: NotepadTextIcon },
  { id: "code", label: "Code", icon: CodeIcon },
  { id: "chart", label: "Chart", icon: ChartColumnIcon },
  { id: "document", label: "Document", icon: FileTextIcon },
  { id: "checklist", label: "Checklist", icon: ListChecksIcon },
  { id: "megaphone", label: "Megaphone", icon: MegaphoneIcon },
  { id: "mail", label: "Mail", icon: MailIcon },
  { id: "calendar", label: "Calendar", icon: CalendarIcon },
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "finance", label: "Finance", icon: LandmarkIcon },
  { id: "legal", label: "Legal", icon: ScaleIcon },
  { id: "terminal", label: "Terminal", icon: SquareTerminalIcon },
];

export const DEFAULT_SKILL_ICON: SkillIcon = "default";

export function skillIconOption(id: string | undefined): SkillIconOption {
  return SKILL_ICON_OPTIONS.find((o) => o.id === id) ?? SKILL_ICON_OPTIONS[0];
}

export interface SkillCategory {
  slug: string;
  name: string;
  icon: SkillIcon;
}

// Same list as the relay's SKILL_MARKETPLACE_CATEGORIES (skill-icons.test.ts keeps them equal).
export const SKILL_CATEGORIES: readonly SkillCategory[] = [
  { slug: "documents", name: "Documents", icon: "document" },
  { slug: "technical-docs", name: "Technical docs", icon: "document" },
  { slug: "knowledge-base", name: "Knowledge base", icon: "document" },
  { slug: "education", name: "Education", icon: "document" },
  { slug: "data-analysis", name: "Data analysis", icon: "chart" },
  { slug: "llm-ai", name: "LLM and AI", icon: "code" },
  { slug: "frontend", name: "Frontend", icon: "code" },
  { slug: "backend", name: "Backend", icon: "code" },
  { slug: "git-workflows", name: "Git workflows", icon: "code" },
  { slug: "code-quality", name: "Code quality", icon: "checklist" },
  { slug: "testing", name: "Testing", icon: "checklist" },
  { slug: "project-management", name: "Project management", icon: "checklist" },
  { slug: "productivity-tools", name: "Productivity", icon: "calendar" },
  { slug: "cicd", name: "CI/CD", icon: "terminal" },
  { slug: "cli-tools", name: "CLI tools", icon: "terminal" },
  { slug: "automation-tools", name: "Automation", icon: "terminal" },
  { slug: "security", name: "Security", icon: "terminal" },
  { slug: "sales-marketing", name: "Sales and marketing", icon: "megaphone" },
  { slug: "content-creation", name: "Content creation", icon: "megaphone" },
  { slug: "design", name: "Design", icon: "image" },
  { slug: "media", name: "Media", icon: "image" },
  { slug: "finance-investment", name: "Finance", icon: "finance" },
  { slug: "real-estate-legal", name: "Legal", icon: "legal" },
];

/** A marketplace skill's icon: its category's, or the default when no marketplace said. */
export function iconForCategory(slug: string | undefined): SkillIcon {
  return SKILL_CATEGORIES.find((c) => c.slug === slug)?.icon ?? DEFAULT_SKILL_ICON;
}
